import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { createOrUpdateCart, getCart, getProduct } from '../firebase/firestore';
import { CartContextType, CartItem, Product } from '../types';

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { currentUser, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) {
      return;
    }
    
    if (currentUser) {
      loadCart();
    } else {
      loadGuestCart();
    }
  }, [currentUser, authLoading]);

  const loadGuestCart = () => {
    try {
      const guestCart = localStorage.getItem('guest-cart');
      if (guestCart) {
        const items = JSON.parse(guestCart);
        setCartItems(items);
      } else {
        setCartItems([]);
      }
    } catch (error) {
      console.error('Error loading guest cart:', error);
      setCartItems([]);
    }
  };

  const loadCart = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      const guestCart = localStorage.getItem('guest-cart');
      const guestItems = guestCart ? JSON.parse(guestCart) : [];
      
      if (currentUser.id === 'demo-user') {
        const demoCart = localStorage.getItem('demo-cart');
        if (demoCart) {
          setCartItems(JSON.parse(demoCart));
        }
        return;
      }
      
      const cart = await getCart(currentUser.id);
      let userItems = cart && cart.items ? cart.items : [];
      
      if (guestItems.length > 0) {
        const mergedItems = [...userItems];
        for (const guestItem of guestItems) {
          const existingIndex = mergedItems.findIndex(item => item.id === guestItem.id);
          if (existingIndex > -1) {
            mergedItems[existingIndex].quantity += guestItem.quantity;
          } else {
            mergedItems.push(guestItem);
          }
        }
        
        setCartItems(mergedItems);
        await saveCart(mergedItems);
        localStorage.removeItem('guest-cart');
      } else {
        setCartItems(userItems);
      }
    } catch (error: any) {
      console.error('Error loading cart:', error);
      
      if (error.code === 'permission-denied') {
        console.warn('🛒 Cart access denied - user may not be properly authenticated');
        setCartItems([]);
        return;
      }
      
      const demoCart = localStorage.getItem('demo-cart');
      if (demoCart) {
        setCartItems(JSON.parse(demoCart));
      }
    } finally {
      setLoading(false);
    }
  };

  const saveCart = async (items: CartItem[]) => {
    if (!currentUser) {
      try {
        localStorage.setItem('guest-cart', JSON.stringify(items));
      } catch (error) {
        console.error('Error saving guest cart:', error);
      }
      return;
    }
    
    try {
      if (currentUser.id === 'demo-user') {
        localStorage.setItem('demo-cart', JSON.stringify(items));
        return;
      }
      
      await createOrUpdateCart(currentUser.id, { items });
    } catch (error) {
      console.error('Error saving cart:', error);
      localStorage.setItem('demo-cart', JSON.stringify(items));
    }
  };

  const addToCart = async (product: Product | any, quantity: number = 1) => {
    if (!product.isAvailable) {
      throw new Error('This product is currently out of stock');
    }

    const existingItemIndex = cartItems.findIndex(item => 
      item.id === product.id && 
      item.selectedWeight?.weight === product.selectedWeight?.weight
    );
    let updatedItems: CartItem[];

    if (existingItemIndex > -1) {
      updatedItems = cartItems.map((item, index) => 
        index === existingItemIndex 
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      const itemPrice = product.selectedWeight?.price || product.price;
      const newCartItem: CartItem = {
        ...product,
        id: product.id,
        productId: product.id,
        productName: product.name,
        productImage: product.primaryImage || product.images?.[0] || '',
        price: itemPrice,
        quantity: quantity,
        unit: product.selectedWeight?.weight || product.unit,
        totalPrice: itemPrice * quantity,
        product: product,
        unitPrice: itemPrice
      };
      updatedItems = [...cartItems, newCartItem];
    }

    setCartItems(updatedItems);
    await saveCart(updatedItems);
  };

  const removeFromCart = async (productId: string) => {
    const updatedItems = cartItems.filter(item => item.id !== productId);
    setCartItems(updatedItems);
    await saveCart(updatedItems);
  };

  const updateQuantity = async (productId: string, quantity: number) => {
    if (quantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    try {
      const freshProduct = await getProduct(productId);
      if (!freshProduct) {
        throw new Error('Product not found');
      }

      if (!freshProduct.isAvailable) {
        throw new Error('This product is currently out of stock');
      }

      const updatedItems = cartItems.map(item => 
        item.id === productId ? { ...item, quantity } : item
      );
      setCartItems(updatedItems);
      await saveCart(updatedItems);
    } catch (error) {
      console.error('Error updating quantity:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    setCartItems([]);
    if (currentUser) {
      await saveCart([]);
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const itemPrice = item.price || item.unitPrice || item.selectedWeight?.price || 0;
      return total + (itemPrice * item.quantity);
    }, 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
